import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import ToggleSwitch from '../buttons/ToggleSwitch';
import CustomSVGs from '../customSVGs/CustomSVGs';
import {
  arrowRightThinSVG,
  clockSVG,
  codeSVG,
  pauseSVG,
  playSVG,
  settingSVG,
  tickWithCircleSVG,
  trendLineSVG,
} from '../../lib/allCustomSVGs';
import '../../styles/cards/aiOptimizationsAutomatedCard.scss';
import { useTranslation } from 'react-i18next';
import { VariantPerformanceData } from '../../lib/api/ai-tracker/get_variant_performance_api_call';

interface CSSChange {
  property: string;
  before: string;
  after: string;
}

interface Props {
  icon: any;
  active?: boolean;
  disabled?: boolean;
  onStatusToggle?: (newStatus: boolean) => void;
  onSettingClick?: () => void;
  onLoadMetrics?: () => void;
  elementName: string;
  elementDescription?: string;
  elementSelector: string;
  cssChanges: CSSChange[];
  conversionRate?: string;
  confidence?: string;
  visitors?: string;
  conversions?: string;
  hasMetrics: boolean;
  status?: 'Pending' | 'Draft' | 'Running' | 'Paused' | 'Completed';
  impactMetrics?: VariantPerformanceData | null;
  isLoadingMetrics?: boolean;
  recommendationId: string;
}

const AIOptimizationsAutomatedCard: React.FC<Props> = ({
  icon,
  active = false,
  disabled = false,
  onStatusToggle,
  onSettingClick,
  onLoadMetrics,
  elementName,
  elementDescription,
  elementSelector,
  cssChanges = [],
  conversionRate = '0%',
  confidence = '0%',
  visitors = '0',
  conversions = '0',
  hasMetrics = false,
  status = 'Draft',
  impactMetrics = null,
  isLoadingMetrics = false,
  recommendationId,
}) => {
  const { t } = useTranslation();
  const [isToggling, setIsToggling] = useState(false);
  const [hasTriggeredMetrics, setHasTriggeredMetrics] = useState(false);

  // Use react-intersection-observer hook for lazy loading
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
    rootMargin: '50px',
  });

  useEffect(() => {
    if (
      inView &&
      !impactMetrics &&
      !isLoadingMetrics &&
      onLoadMetrics &&
      !hasTriggeredMetrics
    ) {
      setHasTriggeredMetrics(true);
      onLoadMetrics();
    }
  }, [
    inView,
    impactMetrics,
    isLoadingMetrics,
    onLoadMetrics,
    recommendationId,
    hasTriggeredMetrics,
  ]);

  const handleStatusToggle = async () => {
    if (!onStatusToggle || isToggling || disabled) return;

    try {
      setIsToggling(true);
      await onStatusToggle(!active);
    } catch (error) {
      console.error('Error toggling status:', error);
    } finally {
      setIsToggling(false);
    }
  };

  // Calculate metrics from impactMetrics data
  const getDisplayMetrics = () => {
    if (!impactMetrics) {
      return {
        conversionRate: conversionRate,
        confidence: confidence,
        visitors: visitors,
        conversions: conversions,
      };
    }

    return {
      conversionRate: impactMetrics.conversions_rate
        ? `${impactMetrics.conversions_rate}%`
        : '0%',
      confidence: impactMetrics.confidence_metrics?.confidence_level
        ? `${impactMetrics.confidence_metrics.confidence_level}%`
        : '0%',
      visitors: impactMetrics.visitors?.toString() || '0',
      conversions: impactMetrics.conversions?.toString() || '0',
    };
  };

  const displayMetrics = getDisplayMetrics();

  return (
    <div className='aiOptimizationsAutomatedCard' ref={ref}>
      {/* Top Info Section */}
      <div className='aiOptimizationsAutomatedCard__info'>
        <div className='aiOptimizationsAutomatedCard__info--content'>
          <div className='aiOptimizationsAutomatedCard__info--content-icon'>
            <CustomSVGs svg={icon} />
          </div>
          <div className='aiOptimizationsAutomatedCard__info--content-titles'>
            <div className='aiOptimizationsAutomatedCard__info--content-titles-main'>
              <h3>{elementName}</h3>
              <div className='aiOptimizationsAutomatedCard__info--content-titles-main-icon'>
                {status === 'Paused' && (
                  <CustomSVGs svg={pauseSVG} fillColor='#f8a61b' />
                )}
                {status === 'Completed' && (
                  <CustomSVGs svg={tickWithCircleSVG} fillColor='#2563eb' />
                )}
                {status === 'Running' && (
                  <CustomSVGs svg={playSVG} fillColor='#16a34a' />
                )}
                {status === 'Pending' && (
                  <CustomSVGs svg={clockSVG} fillColor='#f59e0b' />
                )}
                {status === 'Draft' && (
                  <CustomSVGs svg={clockSVG} fillColor='#71717a' />
                )}
              </div>
            </div>
            <div className='aiOptimizationsAutomatedCard__info--content-titles-details'>
              <div className='aiOptimizationsAutomatedCard__info--content-titles-details-tag'>
                <div>{elementSelector}</div>
              </div>
              {elementDescription && <p>{elementDescription}</p>}
            </div>
          </div>
        </div>

        <div className='aiOptimizationsAutomatedCard__info--config'>
          <div
            className='aiOptimizationsAutomatedCard__info--config-settings'
            onClick={() => onSettingClick && onSettingClick()}
          >
            <CustomSVGs svg={settingSVG} />
          </div>
          <div className='aiOptimizationsAutomatedCard__info--config-toggle'>
            <ToggleSwitch
              isOn={active}
              onClick={handleStatusToggle}
              disabled={isToggling || disabled}
            />
          </div>
        </div>
      </div>

      {/* CSS Changes Section */}
      <div className='aiOptimizationsAutomatedCard--bottom'>
        <div className='aiOptimizationsAutomatedCard__cssChanges'>
          <div className='aiOptimizationsAutomatedCard__cssChanges--title'>
            <CustomSVGs svg={codeSVG} />
            <h3>{t('CSSChanges')}</h3>
          </div>
          <div className='aiOptimizationsAutomatedCard__cssChanges--content'>
            {cssChanges.map((change, index) => (
              <div
                key={index}
                className='aiOptimizationsAutomatedCard__cssChanges--content-item'
              >
                <h4>{change.property}</h4>
                <div className='aiOptimizationsAutomatedCard__cssChanges--content-item-styles'>
                  <div className='aiOptimizationsAutomatedCard__cssChanges--content-item-styles-before'>
                    {change.before}
                  </div>
                  <div className='aiOptimizationsAutomatedCard__cssChanges--content-item-styles-arrow'>
                    <CustomSVGs svg={arrowRightThinSVG} />
                  </div>
                  <div className='aiOptimizationsAutomatedCard__cssChanges--content-item-styles-after'>
                    {change.after}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Impact Metrics Section */}
        <div className='aiOptimizationsAutomatedCard__metrics'>
          <div className='aiOptimizationsAutomatedCard__metrics--title'>
            <CustomSVGs svg={trendLineSVG} />
            <h3>{t('ImpactMetrics')}</h3>
          </div>

          <div className='aiOptimizationsAutomatedCard__metrics--content'>
            {isLoadingMetrics && (
              <div className='aiOptimizationsAutomatedCard__metrics--content-overlay'>
                <div className='aiOptimizationsAutomatedCard__metrics--content-overlay-spinner'>
                  <div className='spinner'></div>
                </div>
              </div>
            )}

            {status === 'Pending' ? (
              <div className='aiOptimizationsAutomatedCard__metrics--content-no'>
                <h3>{t('NoMetricsMessage')}</h3>
              </div>
            ) : (
              <>
                <div className='aiOptimizationsAutomatedCard__metrics--content-item aiOptimizationsAutomatedCard__metrics--content-item-green'>
                  <div className='aiOptimizationsAutomatedCard__metrics--content-item-content'>
                    <span>{t('ConversionRate')}</span>
                    <h3>{displayMetrics.conversionRate}</h3>
                  </div>
                </div>
                <div className='aiOptimizationsAutomatedCard__metrics--content-item aiOptimizationsAutomatedCard__metrics--content-item-orange'>
                  <div className='aiOptimizationsAutomatedCard__metrics--content-item-content'>
                    <span>{t('Conversions')}</span>
                    <h3>{displayMetrics.conversions}</h3>
                  </div>
                </div>
                <div className='aiOptimizationsAutomatedCard__metrics--content-item aiOptimizationsAutomatedCard__metrics--content-item-blue'>
                  <div className='aiOptimizationsAutomatedCard__metrics--content-item-content'>
                    <span>{t('Confidence')}</span>
                    <h3>{displayMetrics.confidence}</h3>
                  </div>
                </div>
                <div className='aiOptimizationsAutomatedCard__metrics--content-item aiOptimizationsAutomatedCard__metrics--content-item-purple'>
                  <div className='aiOptimizationsAutomatedCard__metrics--content-item-content'>
                    <span>{t('Visitors')}</span>
                    <h3>{displayMetrics.visitors}</h3>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIOptimizationsAutomatedCard;
